import { ChevronDown, Search, SquarePen } from 'lucide-react';
import { useMemo, type FC } from 'react';

import { permissions as permifyPermissions } from '@repo/api/permify/constants';
import { Button } from '../../../ui/button';
import { NavigationFilters } from '../../navigation-filters/component';
import { ToolbarSectionProps } from '../types';

import {
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@radix-ui/react-dropdown-menu';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@repo/ui/components/ui/dropdown-menu';
import { useCandidates } from '@/state';
import {
  CANDIDATES_SORT_APPLICANTS_SUFFIX,
  CANDIDATES_SORT_BUTTON_TEST_ID,
  CANDIDATES_SORT_JURY_SUFFIX,
  CANDIDATES_SORT_OPTIONS,
} from '../../../../constants';
export const ToolbarSection: FC<ToolbarSectionProps> = ({
  error,
  filterDefinitions,
  filtersEnabled = true,
  isFilterOpen,
  loading,
  onSearchClick,
  onSelectClick,
  setIsFilterOpen,
  isJuryStage,
  isFinalistsStage,
  openCallId,
}) => {
  const { updateSort, params } = useCandidates();

  const toolBarButtonStyles =
    'border-input md:flex md:items-center md:space-x-2 p-2 md:border';

  const selectedDropdownName = useMemo(() => {
    const selectedDropdownOption = params?.sort;
    if (selectedDropdownOption.averageRating) {
      return selectedDropdownOption.averageRating.name;
    }
    if (selectedDropdownOption.amountOfRatings) {
      return selectedDropdownOption.amountOfRatings.name;
    }
    return selectedDropdownOption.createdAt?.name;
  }, [params]);

  const suffix =
    isJuryStage || isFinalistsStage
      ? CANDIDATES_SORT_JURY_SUFFIX
      : CANDIDATES_SORT_APPLICANTS_SUFFIX;

  const sortOptions = CANDIDATES_SORT_OPTIONS.filter(
    (option: {
      isJuryStage: boolean;
      isFinalistsStage: boolean;
      isApplicantsStage: boolean;
    }) => {
      if (isJuryStage) {
        return option.isJuryStage;
      }
      if (isFinalistsStage) {
        return option.isFinalistsStage;
      }
      return option.isApplicantsStage;
    },
  );
  return (
    <div className="flex w-full h-12 justify-between md:justify-end items-center border-b md:border-none md:gap-2">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            disabled={loading || !!error}
            data-testid={CANDIDATES_SORT_BUTTON_TEST_ID}
          >
            {selectedDropdownName}
            <ChevronDown />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          side="bottom"
          className="p-0 w-[292px]"
        >
          <DropdownMenuLabel className="px-3 py-1.5 font-semibold text-sm">
            Sort by
          </DropdownMenuLabel>
          <DropdownMenuSeparator className="border-t" />
          {sortOptions.map(
            ({
              option,
              testId,
              label,
              needSuffix,
            }: {
              testId: string;
              option: Record<string, unknown>;
              label: string;
              needSuffix: boolean;
            }) => (
              <DropdownMenuItem
                key={label}
                data-testid={testId}
                onSelect={() => updateSort(option)}
                className="px-3 py-1.5"
              >
                {label}
                {needSuffix ? ` ${suffix}` : ''}
              </DropdownMenuItem>
            ),
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <div className="flex items-center space-x-0 md:gap-2">
        {!isJuryStage && (
          <>
            {filtersEnabled && (
              <NavigationFilters
                filterDefinitions={filterDefinitions}
                openCallId={openCallId}
                className={toolBarButtonStyles}
                disabled={loading || !!error}
                onClick={() => {
                  setIsFilterOpen(!isFilterOpen);
                }}
              />
            )}

            {/* Search button hidden temporarily and will be added back after MVP */}
            <Button
              variant="ghost"
              data-permission={permifyPermissions.openCalls.filterAndSearch}
              // className={toolBarButtonStyles}
              className="hidden"
              disabled={loading || !!error}
              onClick={onSearchClick}
            >
              <span className="hidden md:block">Search</span>
              <Search />
            </Button>
            <Button
              variant="ghost"
              data-permission={permifyPermissions.openCalls.bulkAction}
              className={toolBarButtonStyles}
              disabled={loading || !!error}
              onClick={onSelectClick}
            >
              <span className="hidden md:block">Select</span>
              <SquarePen />
            </Button>
          </>
        )}
      </div>
    </div>
  );
};
